import { ListingTags } from "./ListingTags";
import type { DashboardData } from "@/pages/project/use-dashboard-source";
import { PersonLink } from "@/components/common/PersonLink";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { BoxHeader } from "@/components/design/BoxHeader";
import { buttonClasses } from "@/components/design/button-classes";
import { ImageGallery } from "@/components/share/ImageGallery";
import { formatDateTime } from "@/lib/format-date";
import type { ImageValue } from "@/lib/image-data-url";
import { type LocalizedTextLike } from "@/lib/questionnaire-group";
import { ListingField } from "./ListingField";
import { ListingReference } from "./ListingReference";
import { gallerySources } from "./listing-images";
import { useCanManageListing } from "./use-can-manage-listing";

export interface ListingPost {
  contactPerson?: { id: string; formattedName?: string | null; avatar?: { content?: string | null; mimeType?: string | null } | null } | null;
  data?: DashboardData | null;
  id: string;
  reference: string;
  name?: string | null;
  description?: string | null;
  deadline?: string | null;
  publishedOn?: string | null;
  categories: readonly { id: string; name?: LocalizedTextLike | null }[];
  mainImage?: ImageValue | null;
  images?: readonly ImageValue[] | null;
}

// Der "Post" einer Ausschreibung: wie Bewerbende sie lesen, Inhalt links, Kennziffer, Frist und Bewerbung
// rechts. Die Kennziffer steht oben im Kasten "Bewerbung", denn auf sie bezieht sich, wer schreibt.
// Eine Darstellung für beide Seiten: die Bewerber-Seite (OpenListingPage) und die Vorschau aus dem
// Dashboard (ListingPostPage). Was rechts unter Frist und Veröffentlichung noch steht, bringt die Seite.
// Wer die Ausschreibung verwaltet, kommt auf beiden Seiten über "Verwalten" ins Dashboard; der Zurück-Knopf
// der Vorschau geht nur in den Verlauf und führt nicht sicher dorthin.
export function ListingPostView({
  listing,
  fields,
  children,
}: {
  listing: ListingPost;
  /** Weitere Angaben im Kasten "Bewerbung", unter Frist und Veröffentlichung. */
  fields?: ReactNode;
  /** Was unter den Angaben steht (Bewerben, Verweise). */
  children?: ReactNode;
}) {
  const canManage = useCanManageListing(listing.id);
  return <ListingPostLayout listing={listing} fields={fields} canManage={canManage}>{children}</ListingPostLayout>;
}

/**
 * Dieselbe Darstellung ohne jede eigene Anfrage, für die öffentliche Seite ohne Konto: wer dort liest, verwaltet nichts,
 * und eine Anfrage an die angemeldete Schnittstelle gäbe es für ihn nicht (plan 7).
 */
export function ListingPostLayout({
  listing,
  fields,
  canManage,
  children,
}: {
  listing: ListingPost;
  fields?: ReactNode;
  canManage: boolean;
  children?: ReactNode;
}) {
  const intl = useIntl();
  return (
    <>
      <h1 className="smycles-page-title mb-6">
        {listing.name || <FormattedMessage id="share.listings.untitled" defaultMessage="Ohne Titel" />}
      </h1>

      <section className="grid grid-cols-1 gap-6 @lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)] @lg:items-start @lg:gap-8">
        <div className="box bg-surface p-5">
          <BoxHeader title={<FormattedMessage id="share.listings.description" defaultMessage="Beschreibung" />} />
          <ImageGallery className="mb-5 max-w-md" images={gallerySources(listing)} alt={listing.name ?? ""} heroId="listing-hero" />
          <p className="whitespace-pre-line text-smycles-body text-ink">
            {listing.description || (
              <span className="text-ink-muted">
                <FormattedMessage id="share.listings.noDescription" defaultMessage="Keine Beschreibung." />
              </span>
            )}
          </p>
        </div>

        <aside className="box bg-surface p-5">
          <BoxHeader title={<FormattedMessage id="share.openListings.applicationBox" defaultMessage="Bewerbung" />} />
          <div className="space-y-3">
            {listing.contactPerson ? <ListingField label={<FormattedMessage id="share.listings.contactPerson" />}>
              <PersonLink person={{ id: listing.contactPerson.id, name: listing.contactPerson.formattedName ?? "",
                avatarContent: listing.contactPerson.avatar?.content, avatarMime: listing.contactPerson.avatar?.mimeType }} size="sm" />
            </ListingField> : null}
            <ListingReference reference={listing.reference} variant="field" />
            <ListingField label={<FormattedMessage id="share.listings.field.deadline" defaultMessage="Frist" />}>
              {formatDateTime(listing.deadline, intl.locale) || (
                <FormattedMessage id="share.listings.noDeadline" defaultMessage="Keine Frist" />
              )}
            </ListingField>
            <ListingField
              label={<FormattedMessage id="share.listings.field.publishedOn" defaultMessage="Veröffentlicht am" />}
            >
              {formatDateTime(listing.publishedOn, intl.locale) || "–"}
            </ListingField>
            {listing.data ? <ListingTags data={listing.data} /> : null}
            {fields}
          </div>
          {children}
          {canManage ? (
            <div className="mt-5">
              <Link
                id="listing-post-manage"
                to={`/share/listings/${listing.id}`}
                className={buttonClasses({ variant: "ghost", size: "compact", className: "gap-1" })}
              >
                <FormattedMessage id="share.listings.post.manage" defaultMessage="Verwalten" />
                <span aria-hidden="true">↗</span>
              </Link>
            </div>
          ) : null}
        </aside>
      </section>
    </>
  );
}
